import { Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { SEOService } from '@app/core/services/seo.service';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { getPathWithoutLastFragment } from '@app/shared/helpers/location';
import { Promotion } from '@app/shared/models/promotion';
import { Team } from '@app/shared/models/team';
import { Transfer } from '@app/shared/models/transfer';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetRecordOutput } from '@smash-sdk/domain/01-2024';
import { UploaderStatus } from '@smash-sdk/uploader';
import { lastValueFrom, merge, Observable, of, Subscription } from 'rxjs';
import { delay, filter, map, mapTo, take, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { PrepareActions, UploadActions } from '../../store/actions';
import * as fromUploader from '../../store/reducers';
import { isPlatformBrowser } from '@angular/common';
import { CreateTeamDropboxOutput } from '@smash-sdk-private/transfer/01-2024';
import { ThemeSimplified } from '@app/shared/helpers/theme';

@Component({
  selector: 'app-send',
  templateUrl: './send.component.html',
  styleUrls: ['./send.component.scss']
})
export class SendComponent implements OnInit, OnDestroy {

  dropbox$: Observable<CreateTeamDropboxOutput['dropbox']>;
  totalSizeUploaded$: Observable<number>;
  totalSizeToUpload$: Observable<number>;
  promotion$: Observable<Promotion>;
  uploadFormMode$: Observable<string>;
  progress$: Observable<number>;
  remainingTime$: Observable<number>;
  speed$: Observable<number>;
  transfer$: Observable<Transfer>;
  domain$: Observable<GetRecordOutput['record']>;
  isMainDomain$: Observable<boolean>;
  selectedTeam$: Observable<Team>;
  isPremium$: Observable<boolean>;
  uploadStatus$: Observable<UploaderStatus>;
  workflowMode$: Observable<string>;
  theme$: Observable<ThemeSimplified>;

  transferSync: Transfer;
  uploadStatusSync: UploaderStatus;
  showOverlay = false;
  currentDomain: any;
  progressAnimation = of(null);
  state = 'loadingNotStarted';
  loading = false;
  replay = true;
  hasPromotion = false;
  token: string;
  showDiscoverSmash = true;
  showMyAccount = false;
  cancelClicked = false;
  isUploader = false;
  subscriptions: Subscription[] = [];

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private titleService: Title,
    private tokenService: TokenService,
    private router: Router,
    private route: ActivatedRoute,
    private gtmService: GoogleTagManagerService,
    private translateService: TranslateService,
    private seoService: SEOService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {

    if (isPlatformBrowser(this.platformId)) {
      this.promotion$ = this.store$.select(fromUploader.getPromotionLoaded);
      this.theme$ = this.store$.select(fromRoot.getTheme);
      this.selectedTeam$ = this.store$.select(fromRoot.getActiveTeam);
      this.uploadFormMode$ = this.store$.select(fromUploader.getUploadFormMode);
      this.progress$ = this.store$.select(fromUploader.getUploadProgress);
      this.totalSizeToUpload$ = this.store$.select(fromUploader.getTransferSize);
      this.transfer$ = this.store$.select(fromUploader.getTransfer);
      this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
      this.isMainDomain$ = this.store$.select(fromRoot.getIsMainDomain);
      this.isPremium$ = this.store$.select(fromRoot.getIsPremium);
      this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);
      this.dropbox$ = this.store$.select(fromUploader.getDropbox);
      this.workflowMode$ = this.store$.select(fromUploader.getMode);

      this.totalSizeUploaded$ = this.store$.select(fromUploader.getUploadedBytes);
      this.speed$ = this.store$.select(fromUploader.getUploadSpeed);
      this.remainingTime$ = this.speed$.pipe(
        withLatestFrom(
          this.store$.select(fromUploader.getTransferSize),
          this.store$.select(fromUploader.getUploadedBytes),
        ),
        filter(([speed]) => speed > 0),
        map(([speed, sizeToUpload, sizeUploaded]) => {
          const sizeRemaining = sizeToUpload - sizeUploaded;
          const remainingTime = sizeRemaining / speed;
          return remainingTime;
        }),
      );

      this.subscriptions.push(
        this.domain$.subscribe(domain => {
          this.showOverlay = (domain.background.type === 'Youtube' || domain.background.type === 'Vimeo');
          this.currentDomain = domain;
        }),
        this.store$.select(fromRoot.getToken).subscribe((token: string) => {
          this.token = token;
        }),
        this.store$.select(fromUploader.getMode).subscribe((mode: string) => {
          this.isUploader = !!(mode === 'Transfer');
        }),
      );

      if (this.isUploader) {
        this.subscriptions.push(
          this.progress$.subscribe(res => {
            this.titleService.setTitle(res + '% | Smash');
          }),
          this.transfer$.subscribe(transfer => this.transferSync = transfer),
          this.uploadStatus$.subscribe(uploadStatus => {
            if (uploadStatus === UploaderStatus.Finished) {
              this.titleService.setTitle('100 % | Smash');
            }
            this.uploadStatusSync = uploadStatus
          }),
        )
      }

      // show discover Smash
      if (this.token) {
        if (!this.tokenService.isAnonymousAccount(this.token)) {// user is connected
          this.showDiscoverSmash = false;
          this.showMyAccount = true;
          if (this.currentDomain.team) {
            this.showMyAccount = true;
          }
        } else { // user is anonymous
          if (this.currentDomain.team) {
            this.showDiscoverSmash = false;
          }
        }
      }
    }
  }

  canDeactivate(): Observable<boolean> {
    return this.transfer$.pipe(map(transfer => {
      if (transfer?.status === Constant.transferStatus.Uploaded || this.cancelClicked) {
        return true;
      } else {
        return confirm(this.translateService.instant('UPLOADER_SEND_CONFIRM_NAVIGATE_AWAY'));
      }
    }));
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.push(
        this.promotion$.pipe(withLatestFrom(this.theme$)).subscribe(([promotion, theme]) => {
          if (theme?.reveal) {
            this.hasPromotion = true;
            this.resolveLoadingState(this.progressAnimation);
            this.gtmService.pushTag({
              event: Constant.eventGTM.DISPLAY_THEME_REVEAL,
              theme_id: theme.id,
              theme_name: theme.name,
            });

            // TO REMOVE: This is a temporary solution to keep tracking the old promotion display event
            this.gtmService.pushTag({
              event: Constant.eventGTM.INTERNAL_PROMOTION_DISPLAYED,
              promotion_title: theme.name,
              promotion_id: theme.name,
            });
          } else if (promotion) {
            this.hasPromotion = true;
            this.resolveLoadingState(this.progressAnimation);
            this.gtmService.pushTag({
              event: Constant.eventGTM.INTERNAL_PROMOTION_DISPLAYED,
              promotion_title: promotion.title,
              promotion_id: promotion.id,
              promotion_target: promotion.target,
              promotion_link: promotion.link,
              promotion_language: promotion.language,
              smashPromotionTitle: promotion.title,
              smashPromotionId: promotion.id,
              smashPromotionTarget: promotion.target,
              smashPromotionCtaUrl: promotion.link,
              smashPromotionCtaLabel: promotion.button,
              smashPromotionLanguage: promotion.language,
            });
          }
        })
      );
    }
  }

  async onCancelClicked() {
    this.cancelClicked = true;
    const transfer = await this.transfer$.pipe(take(1)).toPromise();
    const progress = await this.progress$.pipe(take(1)).toPromise();
    this.store$.dispatch(UploadActions.CancelUpload());
    this.store$.dispatch(PrepareActions.ResetVerifyEmail());
    this.gtmService.pushTag({
      event: Constant.eventGTM.TRANSFER_CANCELLED,
      transfer_id: transfer?.id,
      transfer_progress: progress,
      file_count: transfer?.filesNumber,
      transfer_size: transfer?.size,
    });
    this.seoService.setLandingTitle();
    this.router.navigate([getPathWithoutLastFragment() + '/prepare'], { relativeTo: this.route });
  }

  async onSmashAgainClicked() {
    this.cancelClicked = true;
    this.seoService.setLandingTitle();
    this.router.navigateByUrl('/');

    if (this.isUploader) {
      const transfer = await lastValueFrom(this.transfer$.pipe(take(1))); //.toPromise();
      this.store$.dispatch(UploadActions.ResetUpload());
      this.store$.dispatch(PrepareActions.ResetVerifyEmail());
      this.gtmService.pushTag({
        event: Constant.eventGTM.SMASH_AGAIN,
        previous_transfer_id: transfer.id,
      });
      this.router.navigate([getPathWithoutLastFragment()], { relativeTo: this.route });
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (this.transferSync?.status !== 'Uploaded') {
      $event.returnValue = true;
    }
  }

  private resolveLoadingState(progress: Observable<null>) {
    this.subscriptions.push(
      this.setAnimation(progress).subscribe((val: string) => {
        this.state = val;
        if (val === 'loadingFinished') {
          this.toggleTeaserMessage();
        }
      })
    );
  }

  private setAnimation(progress: Observable<null>) {
    const message = merge(
      progress.pipe(
        mapTo('loadingStarted'),
        delay(100)
      ),
      progress.pipe(
        mapTo('loadingFinished'),
        delay(2500)
      )
    );
    return message;
  }

  private toggleTeaserMessage() {
    this.replay = true;
    setTimeout(() => {
      this.replay = false;
    }, 2000);
  }

  ngOnDestroy() {
    if (this.transferSync?.status === 'Uploaded') {
      this.store$.dispatch(UploadActions.ResetUpload());
    } else {
      this.store$.dispatch(UploadActions.CancelUpload());
    }
    this.store$.dispatch(UploadActions.CancelUpload());
    this.store$.dispatch(PrepareActions.ResetVerifyEmail());
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}

